import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  /**
   * SESTO. Patch 07.09.2026 (6, QUICK CART REGRESSION FIX) - BUG 2/3.
   * CartAddEvent.eventName is the string 'cart:update', identical to the
   * generic ThemeEvents.cartUpdate used by CartUpdateEvent AND by plain
   * CustomEvent('cart:update', ...) dispatches from our own snippets
   * (sesto-first-sesto-auto-apply.liquid's discount cleanup,
   * sesto-cart-upsell.liquid's AJAX add). Listening by event type alone
   * cannot tell these apart, so any of them previously opened the drawer as
   * a side effect.
   * `event instanceof CartAddEvent` was tried first but proved unreliable
   * live (Claude Chrome test: a genuine PDP add's event still failed the
   * instanceof check on this storefront's asset pipeline). The reliable
   * discriminator is `event.detail.data.source`: assets/product-form.js -
   * the ONLY genuine "Add to Cart" submit path (PDP button and quick-add,
   * both go through ProductFormComponent) - always sets
   * `source: 'product-form-component'` on both the success and the
   * error-path CartAddEvent it dispatches. Every internal SESTO dispatch
   * (discount sync, in-drawer upsell add) and every native quantity/remove
   * CartUpdateEvent use a different `source`, so this allowlist opens the
   * drawer only for a real customer add and leaves every other cart:update
   * alone. showDialog() already no-ops when the dialog is open, so this is
   * additive safety, not a behaviour change for CASE A.
   */
  #handleCartAdd = (event) => {
    if (event.detail?.data?.source !== 'product-form-component') return;

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
